import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { IconDiamond } from "../../assets/icons";

// APIs, Hooks, and Components
import { baseAxios } from "../../apis";
import { formatOrderData } from "../../utils"; // Assuming a utility function
import Drawer from "../commons/Drawer";
import FormBuySellTrading from "./FormBuySellTrading";
import ChartHeader from "./ChartHeader";
import SelectPairModal from "./SelectPairModal";
import LightweightChartWithOrders from "./LightweightChartWithOrders";
import { TradeTableOrderHistory } from "./TradeTableOrderHistory";

// --- HOOKS ---

const useOnClickOutside = (ref, handler) => {
  useEffect(() => {
    const listener = (event) => {
      if (!ref.current || ref.current.contains(event.target)) return;
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    return () => document.removeEventListener("mousedown", listener);
  }, [ref, handler]);
};

// Hook for fetching paginated orders, now co-located with this component.
const usePaperTradingOrdersQuery = (status, page, rowsPerPage = 10) => {
  return useQuery({
    queryKey: ["paperTradingOrders", status, page],
    queryFn: async () => {
      const { data } = await baseAxios.get("/api/paper_trading/orders", {
        params: { status, page, limit: rowsPerPage },
      });
      return {
        results: data.results.map(formatOrderData),
        totalPages: Math.ceil(data.count / rowsPerPage) || 1,
      };
    },
    placeholderData: (previousData) => previousData,
  });
};

// --- MAIN COMPONENT ---

const PaperTradingPage = ({
  summary,
  loadingSummary,
  coins,
  selectedCoin,
  setSelectedCoin,
}) => {
  // UI-specific state
  const [isDesktopDropdownOpen, setIsDesktopDropdownOpen] = useState(false);
  const desktopDropdownRef = useRef(null);
  useOnClickOutside(desktopDropdownRef, () => setIsDesktopDropdownOpen(false));

  const [searchTermDesktop, setSearchTermDesktop] = useState("");
  const [isTradeFormVisible, setIsTradeFormVisible] = useState(false);
  const tradeFormRef = useRef(null);
  useOnClickOutside(tradeFormRef, () => setIsTradeFormVisible(false));

  const [isSelectPairModalOpen, setIsSelectPairModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("buy");

  // State for the orders table is now managed here
  const [openPage, setOpenPage] = useState(1);
  const [filledPage, setFilledPage] = useState(1);

  // Fetch the orders data directly within this component
  const { data: openOrdersData, isLoading: loadingOpen } =
    usePaperTradingOrdersQuery("open", openPage);
  const { data: filledOrdersData, isLoading: loadingFilled } =
    usePaperTradingOrdersQuery("filled", filledPage);

  const openOrders = openOrdersData?.results || [];
  const filledOrders = filledOrdersData?.results || [];

  const filteredCoinsDesktop = coins.filter(
    (coin) =>
      coin.name.toLowerCase().includes(searchTermDesktop.toLowerCase()) ||
      coin.symbol.toLowerCase().includes(searchTermDesktop.toLowerCase())
  );

  const handleSelectCoinDesktop = (coin) => {
    setSelectedCoin(coin);
    setIsDesktopDropdownOpen(false);
    setSearchTermDesktop("");
  };

  const handleSelectCoinFromModal = (coin) => {
    setSelectedCoin(coin);
    setIsSelectPairModalOpen(false);
  };

  const chartRef = useRef(null);
  const buttonBarRef = useRef(null);
  const [hideButtons, setHideButtons] = useState(false);

  useEffect(() => {
    let animationFrameId;
    const checkOverlap = () => {
      const chartRect = chartRef.current?.getBoundingClientRect();
      const buttonRect = buttonBarRef.current?.getBoundingClientRect();
      if (chartRect && buttonRect) {
        setHideButtons(buttonRect.top < chartRect.bottom);
      }
      animationFrameId = requestAnimationFrame(checkOverlap);
    };
    animationFrameId = requestAnimationFrame(checkOverlap);
    return () => cancelAnimationFrame(animationFrameId);
  }, []);

  const mobileBuyButtonStyle = "bg-green-100 text-green-700";
  const mobileSellButtonStyle = "bg-red-100 text-red-700";

  return (
    <div className="w-full min-h-screen overflow-y-hide pb-24 xl:pb-5 mt-4 mb-5">
      <div className="bg-[rgba(255,204,0,0.15)] gap-2 p-3 rounded-xl border border-[#FFCC00] flex items-center">
        <IconDiamond className="w-full max-w-[24px] h-[24px] text-[#8F7303]" />
        <span className="text-sm md:text-base">
          <strong>Paper trading still on demo test.</strong> Lets practice with
          mock money—test strategies, build skills, no risk! A fun way to level
          up!
        </span>
      </div>
      <div className="mt-4 flex flex-col xl:flex-row gap-3">
        <div className="bg-white p-4 rounded-lg w-full xl:w-[70%]">
          <ChartHeader
            selectedCoin={selectedCoin}
            setIsSelectPairModalOpen={setIsSelectPairModalOpen}
            desktopDropdownRef={desktopDropdownRef}
            setIsDesktopDropdownOpen={setIsDesktopDropdownOpen}
            isDesktopDropdownOpen={isDesktopDropdownOpen}
            searchTermDesktop={searchTermDesktop}
            setSearchTermDesktop={setSearchTermDesktop}
            filteredCoinsDesktop={filteredCoinsDesktop}
            handleSelectCoinDesktop={handleSelectCoinDesktop}
          />
          <div
            ref={chartRef}
            id="tradingview_widget_container"
            className=" mt-2 relative z-0"
          >
            <LightweightChartWithOrders
              selectedCoin={selectedCoin}
              ordersHistory={filledOrders} // <-- Correctly passing filled orders for markers
            />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg w-full xl:w-[30%] hidden xl:block">
          <FormBuySellTrading
            setActiveTab={setActiveTab}
            activeTab={activeTab}
            loadingSummary={loadingSummary}
            summary={summary}
            selectedCoin={selectedCoin}
          />
        </div>
      </div>

      {!isTradeFormVisible && (
        <div
          ref={buttonBarRef}
          className={`fixed inset-x-0 shadow-[0px_-2px_5px_#0002] bottom-0 z-40 bg-white p-4 shadow-top xl:hidden ${
            hideButtons && ""
          }`}
        >
          <div className="flex gap-4 w-full">
            <button
              onClick={() => {
                setActiveTab("buy");
                setIsTradeFormVisible(true);
              }}
              className={`flex-1 py-3 rounded-lg text-base font-bold transition-colors ${mobileBuyButtonStyle}`}
            >
              Buy
            </button>
            <button
              onClick={() => {
                setActiveTab("sell");
                setIsTradeFormVisible(true);
              }}
              className={`flex-1 py-3 rounded-lg text-base font-bold transition-colors ${mobileSellButtonStyle}`}
            >
              Sell
            </button>
          </div>
        </div>
      )}

      <TradeTableOrderHistory
        openOrders={openOrders}
        openTotalPages={openOrdersData?.totalPages}
        loadingOpen={loadingOpen}
        openPage={openPage}
        setOpenPage={setOpenPage}
        filledOrders={filledOrders}
        filledTotalPages={filledOrdersData?.totalPages}
        loadingFilled={loadingFilled}
        filledPage={filledPage}
        setFilledPage={setFilledPage}
      />

      <Drawer
        open={isTradeFormVisible}
        onClose={() => setIsTradeFormVisible(false)}
        width="600px"
        className=""
      >
        <div className="mt-[-1.5rem]">
          <div className="flex items-center justify-between w-full md:px-3 sticky top-[-1.25rem] bg-white py-3">
            <button
              className="bg-transparent hover:bg-transparent text-[#007A78] flex !p-0"
              onClick={() => setIsTradeFormVisible(false)}
            ></button>
            <h2 className="text-base capitalize md:text-xl font-bold ">
              {activeTab}
            </h2>
            <button
              className="bg-transparent hover:bg-transparent text-black !p-0"
              onClick={() => setIsTradeFormVisible(false)}
            >
              <XMarkIcon className="w-[20px] h-[20px]" />
            </button>
          </div>
          <div className="pt-3">
            <FormBuySellTrading
              setActiveTab={setActiveTab}
              activeTab={activeTab}
              loadingSummary={loadingSummary}
              summary={summary}
              selectedCoin={selectedCoin}
            />
          </div>
        </div>
      </Drawer>

      <SelectPairModal
        isOpen={isSelectPairModalOpen}
        onClose={() => setIsSelectPairModalOpen(false)}
        onSelectCoin={handleSelectCoinFromModal}
        coinOptions={coins}
        selectedCoin={selectedCoin}
      />
    </div>
  );
};

export default PaperTradingPage;
